import { useEffect, useRef, useState } from 'react'

import Action from 'ageas-react-component-action'
import MainLoader from 'ageas-react-component-loader'
import Modal from 'ageas-react-component-modal'
import PageTitle from 'ageas-react-component-pagetitle'

import { MainContainer } from '../../GlobalStyles'
import { useReportStore } from '../../Stores/reportStore'
import { useSimulatorStore } from '../../Stores/simulatorStore'
import { useCreatePolicyHolderEntityStore } from '../../Stores/useCreatePolicyholderEntityStore'
import { useLoadPolicyHolderReferenceDataStore } from '../../Stores/useLoadPolicyHolderReferenceDataStore'
import { useCreatePolicyholderEntity } from '../../hooks/api/useCreatePolicyholderEntity'
import { t } from '../../i18n'
import { validateEmail, validatePhoneNumber } from '../../utils/validates'
import { Disclaimer } from '../Disclaimer'
import { ModalComponent } from '../ModalComponent'
import { HandleActionProp, Tab } from '../Tab'
import { TabItem } from '../Tab/TabItem'
import {
  AddressForm,
  FormAddressValuesProps,
  initialFormAddressValues,
} from './components/AddressForm'
import {
  ContactForm,
  FormContactValuesProps,
  MOBILE,
  initialContactFormValues,
} from './components/ContactForm'
import {
  FormIdentificationValuesProps,
  IdentificationForm,
  initialFormIdentificationValues,
} from './components/IdentificationForm'
import { handleCreatePolicyPayload } from './handleCreatePolicyPayload'

type CreateEntityProps = {
  isVisible: boolean
  closeAction?: () => void
}

export interface FormValuesProps {
  vatNumber: string | null
  gender: string
  birthdate: string
  nationality: string
  entityJob: string
  entityJobDescription: string
  name: string
}

const PHONE_ID = '2'

export const validateMandatoryFields = (
  data: Record<string, string>,
  fieldsMandatory: string[],
): boolean => {
  for (const field of fieldsMandatory) {
    if (!data[field] || data[field].trim() === '') {
      return false
    }
  }
  return true
}

export function CreateEntity({
  isVisible,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  closeAction = () => {},
}: CreateEntityProps) {
  const createEntityTabRef = useRef<HandleActionProp>(null)
  const [currentTab, setCurrentTab] = useState(0)
  const [disabledTabs, setDisabledTabs] = useState([1, 2])
  const { ownerFiscalNumber, setOwnerFiscalNumber } = useSimulatorStore()
  const { isProspect, report } = useReportStore()
  const { setIsPolicyholderCreated } = useCreatePolicyHolderEntityStore()
  const { policyHolderReferenceData } = useLoadPolicyHolderReferenceDataStore()

  const getPhoneById = (policyHolderReferenceData?.contactTypes || [])?.find(
    (contact) => contact.idElement === PHONE_ID,
  )

  const initialIdentificationValues = isProspect
    ? {
        ...initialFormIdentificationValues,
        name: report.name || '',
      }
    : initialFormIdentificationValues

  const initialContactValues = isProspect
    ? {
        ...initialContactFormValues,
        email: report.email || '',
        contactPhone: getPhoneById?.idElement || '',
        contactDescription: getPhoneById?.description || '',
        contactPhoneNumber: report.phone || '',
      }
    : initialContactFormValues

  const [formIdentificationValues, setFormIdentificationValues] =
    useState<FormIdentificationValuesProps>(initialIdentificationValues)
  const [formAddressValues, setFormAddressValues] =
    useState<FormAddressValuesProps>(initialFormAddressValues)
  const [formContactValues, setFormContactValues] =
    useState<FormContactValuesProps>(initialContactValues)
  const { mutate, isSuccess, isLoading } = useCreatePolicyholderEntity()

  const fieldsContactMandatory = ['email', 'contactPhone', 'contactPhoneNumber']

  const handleCloseAction = () => {
    setFormIdentificationValues(initialFormIdentificationValues)
    setFormAddressValues(initialFormAddressValues)
    setFormContactValues(initialContactFormValues)
    closeAction()
  }

  useEffect(() => {
    if (isSuccess) {
      setIsPolicyholderCreated(true)
      setOwnerFiscalNumber(formIdentificationValues.vatNumber!)
      handleCloseAction()
    }
  }, [isSuccess])

  return (
    <ModalComponent show={isVisible}>
      <>
        <MainLoader show={isLoading} />
        <Modal
          show={isVisible}
          hideCloseAction={false}
          closeAction={handleCloseAction}
          content={
            <MainContainer>
              <PageTitle
                id="createEntity"
                name={t('pages.step3.createEntity.newEntity')}
              />
              <Tab
                ref={createEntityTabRef}
                buttonLabel={t('pages.step3.createEntity.next')}
                onChange={setCurrentTab}
                initialTab={currentTab}
                disabledTabs={disabledTabs}
                tabs={[
                  <TabItem label={t('pages.step3.identification.title')} />,
                  <TabItem label={t('pages.step3.addresses.title')} />,
                  <TabItem label={t('pages.step3.contacts.title')} />,
                ]}
                pages={[
                  <IdentificationForm
                    key="IdentificationForm"
                    initialFormValues={formIdentificationValues}
                    initialOwnerFiscalNumber={ownerFiscalNumber}
                    onNextChange={() => {
                      createEntityTabRef.current?.handleNextStep()
                      setDisabledTabs([2])
                    }}
                    onChange={(values) => setFormIdentificationValues(values)}
                  />,
                  <>
                    <AddressForm
                      isCreateEntity
                      key="AddressForm"
                      initialFormValues={formAddressValues}
                      onNextChange={() => {
                        createEntityTabRef.current?.handleNextStep()
                        setDisabledTabs([])
                      }}
                      onChange={(values) => setFormAddressValues(values)}
                    />
                    {formAddressValues.entityLocalization.length === 0 && (
                      <>
                        <Disclaimer
                          text={t('addressSelectTable.disclaimerInfo')}
                        />
                        <Disclaimer
                          text={t('addressSelectTable.disclaimerFont')}
                        />
                      </>
                    )}
                  </>,
                  <ContactForm
                    key="ContactForm"
                    initialFormValues={formContactValues}
                    onChange={(values) => setFormContactValues(values)}
                  />,
                ]}
              />
              {currentTab === 2 && (
                <Action
                  id="newEntityCreateButton"
                  label={t('pages.step3.createEntity.newEntity')}
                  type="button"
                  disable={
                    !validateEmail(formContactValues.email || '') ||
                    !validatePhoneNumber(
                      formContactValues.contactPhoneNumber,
                      formContactValues.contactPhone === MOBILE,
                    ) ||
                    !validateMandatoryFields(
                      formContactValues as never,
                      fieldsContactMandatory,
                    )
                  }
                  onValueChanged={() => {
                    mutate(
                      handleCreatePolicyPayload({
                        formIdentificationValues,
                        formAddressValues,
                        formContactValues,
                      }),
                    )
                  }}
                />
              )}
            </MainContainer>
          }
        />
      </>
    </ModalComponent>
  )
}
